import React from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { PaymentCheckoutPage, PurchaseInfo } from '@lialo/payment/lib/checkout/tour';
import routes from '@lialo/core/lib/pages/utils/routes';
import { tourDetailSelectors } from '@lialo/core/lib/pages/tourDetail';
import { RootState } from '@lialo/core/lib/store/root';
import { commonActions } from '@lialo/core/lib/pages/common';
import { chooseTourImageLink } from '@lialo/core/lib/utils/helper/image-link';

import { AppBar } from '@lialo/pwa-ui/lib/PageComponents/AppBar';
import { Page } from '@lialo/pwa-ui/lib/PageComponents/Page';
import { RequireUser } from '../../../common/RequireUser';
import { PaymentConfigProvider } from '../../../app/PaymentConfigProvider';
import RouterBackButton from '../../../common/Page/AppBarButtons/RouterBackButton';
import TourCheckoutSummary from './TourCheckoutSummary';
import { getRobotsValue } from '../../../services/utils';
import { useServiceProvider } from '../../../common/Providers';
import { useCheckLocalStorageAvailability } from '../../../common/hooks/useCheckLocalStorageAvailability';

const StyledDiv = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  flexGrow: 1,
  // scrollBehavior: 'smooth', // disabled, because scrollIntoView() doesn't work otherwise
  padding: theme.spacing(2),
}));

const TourCheckoutPage = () => {
  const { t } = useTranslation();

  const { analyticsService, navigationService } = useServiceProvider();

  const { tourCode } = useParams<{ tourCode: string }>();
  const tour = useSelector(tourDetailSelectors.selectTourDetails);
  useCheckLocalStorageAvailability();

  React.useEffect(() => {
    if (tour) {
      analyticsService.logPlayShowCheckout(tour.shortLink, tour.price, tour.user?.id, tour.startLocation?.city);
    }
  }, [analyticsService, tour]);

  const onPurchase = (info: PurchaseInfo) => {
    analyticsService.logPaymentCompleted(
      tourCode,
      info.paymentMethod,
      info.price.currentPrice,
      tour?.user?.id,
      tour?.startLocation?.city
    );
    analyticsService.logPurchase(
      tourCode,
      tour?.title,
      info.price.currentPrice,
      info.price.appliedDiscount?.code,
      tour?.price,
      info.orderId ?? undefined,
      tour?.user?.id,
      tour?.startLocation?.city
    );
    navigationService.navigate(navigationService.generatePlayTourUrl(routes.PLAY_TOUR, tourCode, true), {
      replace: true,
    });
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content={getRobotsValue('noindex,nofollow')} />
      </Helmet>
      <RequireUser parentUrl={navigationService.generateTourDetailUrl(tourCode)}>
        <Page>
          <AppBar
            title={t('tour_detail.checkout.title')}
            leftElement={<RouterBackButton parentUrl={navigationService.generateTourDetailUrl(tourCode)} />}
          />
          <StyledDiv>
            {tour && tour.title && tour.price && (
              <>
                <TourCheckoutSummary
                  imageSrc={chooseTourImageLink(tour.mainImages[0]?.url, 500)}
                  title={tour.title}
                  numberOfStops={tour.numberOfStops}
                  numberOfTasks={tour.numberOfTasks}
                  price={tour.price}
                  distance={tour.settings?.distance}
                  duration={tour.settings?.duration}
                  averageRating={tour.rating}
                />
                <PaymentConfigProvider>
                  <PaymentCheckoutPage
                    shortLink={tour.shortLink}
                    productName={tour.title}
                    price={tour.price}
                    onPurchase={onPurchase}
                    onPaymentMethodSelected={type =>
                      analyticsService.logPaymentMethodSelected(
                        tourCode,
                        type,
                        tour.price,
                        tour.user?.id,
                        tour.startLocation?.city
                      )
                    }
                  />
                </PaymentConfigProvider>
              </>
            )}
          </StyledDiv>
        </Page>
      </RequireUser>
    </>
  );
};

export default TourCheckoutPage;
